/**
 * AssistantSubmenu — the expandable pane under the Assistant nav row.
 *
 * Contains: New Chat, Search trigger, Projects (tree with rename /
 * delete / more-menu), Recent Chats (direct + project, merged, sorted).
 *
 * Phase E1: extracted from Sidebar.tsx.
 */

import { useMemo, type UIEventHandler } from 'react';
import {
    Plus, Folder, MessageSquare, MoreHorizontal, ChevronDown, Search,
} from 'lucide-react';
import TypewriterText from './TypewriterText';
import type { ChatMetadata } from '../../../modules/chat/chatMetadataService';
import type { Project, ContextMenuTarget } from './types';
import { SkeletonList } from '../../ui/skeleton';
import { GLOSSARY } from '../../../modules/mentorship/glossary';

type Props = {
    selectedChatId?: string | undefined;
    selectedProjectId?: string | undefined;
    projects: Project[];
    directChats: ChatMetadata[];
    searchQuery: string;
    isProjectsExpanded: boolean;
    isYourChatsExpanded: boolean;
    expandedProjects: Set<string>;
    animatingChatIds: Set<string>;
    renamingProjectId: string | null;
    renameProjectValue: string;
    renamingChatId: string | null;
    renameValue: string;
    isLoadingMoreDirectChats: boolean;

    onNewDirectChat?: (() => void) | undefined;
    onChatSelect?: ((projectId: string, chatId: string) => void) | undefined;
    onOpenSearch: () => void;
    onToggleProjects: () => void;
    onToggleRecentChats: () => void;
    onToggleProject: (projectId: string) => void;
    onAddProject: () => void;
    onMoreClick: (e: React.MouseEvent<HTMLButtonElement>,
                  type: ContextMenuTarget['type'], id: string, projectId?: string) => void;
    onCreateProjectChat: (projectId: string) => void;

    onRenameProjectChange: (value: string) => void;
    onCommitRenameProject: (projectId: string) => void;
    onCancelRenameProject: () => void;

    onRenameChatChange: (value: string) => void;
    onCommitRenameChat: (chatId: string, projectId?: string) => void;
    onCancelRenameChat: () => void;

    onAnimationDone: (chatId: string) => void;
    onScroll: UIEventHandler<HTMLDivElement>;
    closeSidebarOnMobile: () => void;
};

export default function AssistantSubmenu(props: Props) {
    const {
        selectedChatId, selectedProjectId,
        projects, directChats, searchQuery,
        isProjectsExpanded, isYourChatsExpanded, expandedProjects, animatingChatIds,
        renamingProjectId, renameProjectValue, renamingChatId, renameValue,
        isLoadingMoreDirectChats,
        onNewDirectChat, onChatSelect, onOpenSearch,
        onToggleProjects, onToggleRecentChats, onToggleProject,
        onAddProject, onMoreClick, onCreateProjectChat,
        onRenameProjectChange, onCommitRenameProject, onCancelRenameProject,
        onRenameChatChange, onCommitRenameChat, onCancelRenameChat,
        onAnimationDone, onScroll, closeSidebarOnMobile,
    } = props;

    const sq = searchQuery.toLowerCase().trim();

    const filteredProjects = useMemo(() => (
        sq
            ? projects.filter((p) =>
                p.name.toLowerCase().includes(sq)
                || p.chats.some((c) => c.name?.toLowerCase().includes(sq)))
            : projects
    ), [projects, sq]);

    const allRecentChats = useMemo(() => (
        [
            ...directChats.map((c) => ({ id: c.id, name: c.name, updatedAt: '', projectId: '', projectName: '' })),
            ...projects.flatMap((p) => p.chats.map((c) => ({
                id: c.id, name: c.name, updatedAt: c.updatedAt || '',
                projectId: p.id, projectName: p.name,
            }))),
        ]
            .filter((c) => !sq
                || c.name?.toLowerCase().includes(sq)
                || c.projectName?.toLowerCase().includes(sq))
            .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
    ), [directChats, projects, sq]);

    return (
        <div className="submenu sidebar-chat-panel" onScroll={onScroll}>
            <button
                type="button"
                className={`submenu-item ${!selectedChatId && !selectedProjectId ? 'active' : ''}`}
                onClick={() => { onNewDirectChat?.(); closeSidebarOnMobile(); }}
            >
                <Plus size={16} />
                <span>New Chat</span>
            </button>
            <button
                type="button"
                className="submenu-item"
                onClick={() => { closeSidebarOnMobile(); onOpenSearch(); }}
            >
                <Search size={14} />
                <span>Search</span>
            </button>

            <div className="submenu-section-header">
                <button
                    type="button"
                    className="submenu-section-toggle"
                    onClick={onToggleProjects}
                >
                    <ChevronDown size={12} className={`submenu-chevron ${isProjectsExpanded ? 'expanded' : ''}`} />
                    <span>{GLOSSARY.PROJECT.pluralLabel}</span>
                    {filteredProjects.length > 0 && (
                        <span className="submenu-section-count">{filteredProjects.length}</span>
                    )}
                </button>
                <button
                    type="button"
                    className="submenu-section-add"
                    title={`New ${GLOSSARY.PROJECT.shortLabel}`}
                    onClick={onAddProject}
                >
                    <Plus size={14} />
                </button>
            </div>

            {isProjectsExpanded && filteredProjects.map((project) => (
                <div key={project.id}>
                    <div className="submenu-item-wrap">
                        {renamingProjectId === project.id ? (
                            <ProjectRenameRow
                                value={renameProjectValue}
                                onChange={onRenameProjectChange}
                                onCommit={() => onCommitRenameProject(project.id)}
                                onCancel={onCancelRenameProject}
                            />
                        ) : (
                            <button
                                type="button"
                                className={`submenu-item submenu-item--indent ${selectedProjectId === project.id ? 'active' : ''}`}
                                onClick={() => onToggleProject(project.id)}
                            >
                                <ChevronDown size={12} className={`submenu-chevron ${expandedProjects.has(project.id) ? 'expanded' : ''}`} />
                                <Folder size={14} />
                                <span>{project.name}</span>
                            </button>
                        )}
                        {renamingProjectId !== project.id && (
                            <button
                                type="button"
                                className="submenu-more-btn"
                                title="More options"
                                // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value for ContextMenuTarget union (infrastructure id), not user copy.
                                onClick={(e) => { e.stopPropagation(); onMoreClick(e, 'project', project.id); }}
                            >
                                <MoreHorizontal size={14} />
                            </button>
                        )}
                    </div>
                    {expandedProjects.has(project.id) && (
                        <div className="submenu-nested">
                            {project.chats.map((chat) => (
                                <div key={chat.id} className="submenu-item-wrap">
                                    {renamingChatId === chat.id ? (
                                        <ChatRenameRow
                                            deep
                                            value={renameValue}
                                            onChange={onRenameChatChange}
                                            onCommit={() => onCommitRenameChat(chat.id, project.id)}
                                            onCancel={onCancelRenameChat}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className={`submenu-item submenu-item--deep ${selectedChatId === chat.id ? 'active' : ''}`}
                                            onClick={() => { onChatSelect?.(project.id, chat.id); closeSidebarOnMobile(); }}
                                        >
                                            <MessageSquare size={12} />
                                            {animatingChatIds.has(chat.id) ? (
                                                <TypewriterText text={chat.name} onDone={() => onAnimationDone(chat.id)} />
                                            ) : (
                                                <span>{chat.name}</span>
                                            )}
                                        </button>
                                    )}
                                    {renamingChatId !== chat.id && (
                                        <button
                                            type="button"
                                            className="submenu-more-btn"
                                            title="More options"
                                            // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value for ContextMenuTarget union (infrastructure id), not user copy.
                                            onClick={(e) => { e.stopPropagation(); onMoreClick(e, 'project-chat', chat.id, project.id); }}
                                        >
                                            <MoreHorizontal size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`submenu-item submenu-item--deep ${selectedProjectId === project.id && !selectedChatId ? 'active' : 'submenu-item--muted'}`}
                                onClick={() => { onCreateProjectChat(project.id); closeSidebarOnMobile(); }}
                            >
                                <Plus size={12} />
                                <span>New Chat</span>
                            </button>
                        </div>
                    )}
                </div>
            ))}

            <div className="submenu-section-header">
                <button
                    type="button"
                    className="submenu-section-toggle"
                    onClick={onToggleRecentChats}
                >
                    <ChevronDown size={12} className={`submenu-chevron ${isYourChatsExpanded ? 'expanded' : ''}`} />
                    <span>Recent Chats</span>
                </button>
            </div>

            {isYourChatsExpanded && allRecentChats.map((chat) => (
                <div key={chat.id} className="submenu-item-wrap">
                    {renamingChatId === chat.id ? (
                        <ChatRenameRow
                            value={renameValue}
                            onChange={onRenameChatChange}
                            onCommit={() => onCommitRenameChat(chat.id)}
                            onCancel={onCancelRenameChat}
                        />
                    ) : (
                        <button
                            type="button"
                            className={`submenu-item submenu-item--indent ${selectedChatId === chat.id ? 'active' : ''}`}
                            onClick={() => { onChatSelect?.(chat.projectId, chat.id); closeSidebarOnMobile(); }}
                        >
                            <MessageSquare size={14} />
                            {animatingChatIds.has(chat.id) ? (
                                <TypewriterText text={chat.name || 'New chat'} onDone={() => onAnimationDone(chat.id)} />
                            ) : (
                                <span>{chat.name || 'New chat'}</span>
                            )}
                            {chat.projectName && (
                                <span className="chat-project-tag">{chat.projectName}</span>
                            )}
                        </button>
                    )}
                    {renamingChatId !== chat.id && (
                        <button
                            type="button"
                            className="submenu-more-btn"
                            title="More options"
                            onClick={(e) => { e.stopPropagation(); onMoreClick(e, 'direct-chat', chat.id); }}
                        >
                            <MoreHorizontal size={14} />
                        </button>
                    )}
                </div>
            ))}

            {isLoadingMoreDirectChats && <SkeletonList count={2} item="row" gap="sm" className="sp-loading-hint" />}
        </div>
    );
}

type RenameRowProps = {
    value: string;
    onChange: (v: string) => void;
    onCommit: () => void;
    onCancel: () => void;
    deep?: boolean;
};

function ProjectRenameRow({ value, onChange, onCommit, onCancel }: RenameRowProps) {
    return (
        <div className="submenu-item submenu-item--indent">
            <ChevronDown size={12} className="submenu-chevron submenu-chevron--hidden" />
            <Folder size={14} />
            <input
                className="submenu-rename-input"
                value={value}
                placeholder={`${GLOSSARY.PROJECT.shortLabel} name`}
                autoFocus
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onCommit();
                    if (e.key === 'Escape') onCancel();
                }}
                onBlur={onCommit}
            />
        </div>
    );
}

function ChatRenameRow({ value, onChange, onCommit, onCancel, deep }: RenameRowProps) {
    return (
        <div className={deep ? 'submenu-item submenu-item--deep' : 'submenu-item submenu-item--indent'}>
            <MessageSquare size={deep ? 12 : 14} />
            <input
                className="submenu-rename-input"
                value={value}
                placeholder="Chat name"
                autoFocus
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onCommit();
                    if (e.key === 'Escape') onCancel();
                }}
                onBlur={onCommit}
            />
        </div>
    );
}
